import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button, Spinner, Form, Card, Row, Col } from 'react-bootstrap'
import Cookies from 'js-cookie'
import toast from 'react-hot-toast'
import ApiEndpoints, { BASE_URL } from './commonComponents/ApiEndpoints'
import { useInstitute } from './commonComponents/InstituteContext'
import { useSidebar } from './SidebarContext'
import { useAuth } from './AuthContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { LuGraduationCap } from 'react-icons/lu'
import { BsEyeFill, BsSearch, BsFunnelFill } from 'react-icons/bs'
import html2pdf from 'html2pdf.js'
import html2canvas from 'html2canvas'
import axios from 'axios'
import AdmitCardPrintView from './AdmitCardPrintView'
import AdmitCardShriHit1 from '../AdmitCardShriHit1'
import {
  NO_IMAGE_SRC,
  resolveInstituteLogoSrc,
  getInstituteLogoPathFromRecord,
} from '../utils/instituteBranding'
import {
  canvasToShareImage,
  isReactNativeWebView,
  nativeShareErrorMessage,
  requestNativeImageShare,
  shareFilesWithWebShare,
} from '../utils/nativeImageShare'
import {
  normalizeId,
  formatDate,
  getFilteredSubjects,
  getSubjectRows,
  buildExamRows,
  mapSubjectSchedulesToAdmitRows,
} from './admitCardHelpers'

/** Matches API check for Shri Hit admit card template */
const isShriHitAdmitCardCode = (code) => {
  const normalized = String(code || '').toUpperCase()
  return normalized === 'SHRIHITADMITCARD1'
}

const hasShriHitAdmitCardTemplate = (templates) => {
  if (!Array.isArray(templates)) return false
  return templates.some(
    (item) =>
      String(item?.template_type || '').toLowerCase() === 'admit_card' &&
      isShriHitAdmitCardCode(item?.template_code),
  )
}

const statusBadgeStyle = (status) => {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'upcoming') {
    return { background: '#FEF3C7', color: '#92400E' }
  }
  if (normalized === 'cancel' || normalized === 'cancelled') {
    return { background: '#FEE2E2', color: '#991B1B' }
  }
  if (normalized === 'completed' || normalized === 'complete') {
    return { background: '#DCFCE7', color: '#166534' }
  }
  return { background: '#E5E7EB', color: '#374151' }
}

const blobToDataURL = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const fetchImageAsDataUrlForPdf = async (url) => {
  if (!url || url.startsWith('data:')) return url
  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Direct logo fetch failed for PDF:', error)
  }

  try {
    const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const response = await fetch(proxyUrl)
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('Proxy logo fetch failed for PDF:', error)
  }

  try {
    const response = await fetch(
      `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    )
    if (response.ok) return await blobToDataURL(await response.blob())
  } catch (error) {
    console.warn('AllOrigins logo fetch failed for PDF:', error)
  }

  return url
}

/** Inline remote images so html2canvas can paint them without CORS blanks. */
async function inlineImagesForCapture(rootEl) {
  const imgs = [...rootEl.querySelectorAll('img')]
  const backups = []
  for (const img of imgs) {
    const current = img.currentSrc || img.src
    if (!current || current.startsWith('data:')) continue
    backups.push([img, current])
    try {
      const dataUrl = await fetchImageAsDataUrlForPdf(current)
      img.src = dataUrl
      if (img.decode) await img.decode().catch(() => {})
    } catch {
      backups.pop()
    }
  }
  return () => {
    for (const [img, src] of backups) {
      img.src = src
    }
  }
}

const AdmitCard = () => {
  const { studentId, examId: examIdParam } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    name: instituteNameFromContext,
    logo: instituteLogoFromContext,
    id: instituteIdFromContext,
  } = useInstitute()
  const instituteId = instituteIdFromContext || Cookies.get('institute_id')
  const { isCollapsed } = useSidebar()
  const { logout, user } = useAuth()

  const [student, setStudent] = useState(null)
  const [subjects, setSubjects] = useState([])
  const [exams, setExams] = useState([])
  const [enrollments, setEnrollments] = useState([])
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [subjectSchedules, setSubjectSchedules] = useState([])
  const [scheduleExamLocation, setScheduleExamLocation] = useState('')
  const [selectedExamId, setSelectedExamId] = useState('')
  const [viewMode, setViewMode] = useState('list') // 'list' | 'view'
  const currentSession =
    Cookies.get('session_name') || Cookies.get('session') || ''
  const [selectedSession, setSelectedSession] = useState('')
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingSchedules, setLoadingSchedules] = useState(false)
  const [error, setError] = useState('')
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [pdfInstituteLogo, setPdfInstituteLogo] = useState('')
  const [fetchedInstituteLogo, setFetchedInstituteLogo] = useState('')
  const [exporting, setExporting] = useState(false)
  const [useShriHitTemplate, setUseShriHitTemplate] = useState(false)
  const admitCardRef = useRef(null)
  const appliedExamFromStateRef = useRef(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    // Template choice is only needed on the admit-card view route
    if (!examIdParam) {
      setUseShriHitTemplate(false)
      return
    }

    let cancelled = false

    const fetchTemplateCodes = async () => {
      const resolvedInstituteId = Number(instituteId)
      const token = user?.token

      if (!Number.isFinite(resolvedInstituteId) || resolvedInstituteId <= 0 || !token) {
        if (!cancelled) setUseShriHitTemplate(false)
        return
      }

      try {
        const response = await axios.get(`${BASE_URL}/templates/codes`, {
          params: { template_type: 'admit_card' },
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
            'Institute-Id': resolvedInstituteId,
          },
        })

        const templates = Array.isArray(response.data?.data) ? response.data.data : []
        if (!cancelled) {
          setUseShriHitTemplate(hasShriHitAdmitCardTemplate(templates))
        }
      } catch {
        if (!cancelled) setUseShriHitTemplate(false)
      }
    }

    fetchTemplateCodes()
    return () => {
      cancelled = true
    }
  }, [user?.token, instituteId, examIdParam])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError('')
      // Clear list-only enrichment caches (not needed for /student/:id/admit-card/:examId)
      setSubjects([])
      setEnrollments([])
      setCourses([])
      setClasses([])
      try {
        // List + view both use student exams (includes student, exams, nested schedules)
        const studentExamsRes = await ApiEndpoints.getStudentExams(studentId)
        const payload = studentExamsRes?.data || {}
        setStudent(payload.student || null)
        setExams(Array.isArray(payload.data) ? payload.data : [])
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load admit card data.')
      } finally {
        setLoading(false)
      }
    }

    if (studentId) fetchData()
  }, [studentId, instituteId, user?.token])

  const instituteName =
    instituteNameFromContext || Cookies.get('name') || Cookies.get('institute_name') || ''

  const instituteDisplayLogo = useMemo(() => {
    const toHttps = (url) =>
      typeof url === 'string' ? url.replace(/^http:\/\//i, 'https://') : url

    if (instituteLogoFromContext && instituteLogoFromContext !== NO_IMAGE_SRC) {
      return toHttps(instituteLogoFromContext)
    }
    const cookieLogo = Cookies.get('institute_logo')
    if (cookieLogo) {
      const cookieResolved = resolveInstituteLogoSrc(cookieLogo)
      if (cookieResolved && cookieResolved !== NO_IMAGE_SRC) {
        return toHttps(cookieResolved)
      }
    }
    if (fetchedInstituteLogo) {
      const fetchedResolved = resolveInstituteLogoSrc(fetchedInstituteLogo)
      if (fetchedResolved && fetchedResolved !== NO_IMAGE_SRC) {
        return toHttps(fetchedResolved)
      }
    }
    return NO_IMAGE_SRC
  }, [instituteLogoFromContext, fetchedInstituteLogo])

  const headerLogoSrc =
    instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC
      ? instituteDisplayLogo
      : `${process.env.PUBLIC_URL || ''}/logo192.png`
  // Prefer data-URL for print/PDF (CORS-safe); keep remote URL for on-screen header
  const instituteLogoSrc = pdfInstituteLogo || headerLogoSrc

  useEffect(() => {
    setLogoError(false)
  }, [headerLogoSrc])

  useEffect(() => {
    if (instituteDisplayLogo && instituteDisplayLogo !== NO_IMAGE_SRC) return
    if (!instituteId) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await ApiEndpoints.getAllInstitutesAssinged()
        const list = Array.isArray(res?.data?.data) ? res.data.data : []
        const matched = list.find(
          (row) =>
            String(row?.institute_id ?? row?.id ?? '') === String(instituteId),
        )
        const logoPath = getInstituteLogoPathFromRecord(matched)
        if (!cancelled && logoPath) {
          const resolved = resolveInstituteLogoSrc(logoPath)
          setFetchedInstituteLogo(resolved)
          if (resolved && resolved !== NO_IMAGE_SRC) {
            Cookies.set('institute_logo', resolved, { expires: 7 })
          }
        }
      } catch {
        // keep placeholder branding
      }
    })()

    return () => {
      cancelled = true
    }
  }, [instituteId, instituteDisplayLogo])

  useEffect(() => {
    let isMounted = true
    const prepareLogoForPdf = async () => {
      if (!instituteDisplayLogo || instituteDisplayLogo === NO_IMAGE_SRC) {
        if (isMounted) setPdfInstituteLogo('')
        return
      }
      const resolved = await fetchImageAsDataUrlForPdf(instituteDisplayLogo)
      if (isMounted && resolved) setPdfInstituteLogo(resolved)
    }
    prepareLogoForPdf()
    return () => {
      isMounted = false
    }
  }, [instituteDisplayLogo])

  const courseNameById = useMemo(() => {
    const map = new Map()
    courses.forEach((course) => {
      const id = normalizeId(course?.id)
      if (id) map.set(id, course?.name || `Course ${id}`)
    })
    enrollments.forEach((row) => {
      const id = normalizeId(row?.course_id)
      if (id && !map.has(id)) {
        map.set(id, row?.course_name || row?.course?.name || `Course ${id}`)
      }
    })
    return map
  }, [courses, enrollments])

  const classNameById = useMemo(() => {
    const map = new Map()
    classes.forEach((cls) => {
      const id = normalizeId(cls?.id)
      if (id) map.set(id, cls?.class_name || cls?.name || `Class ${id}`)
    })
    enrollments.forEach((row) => {
      const id = normalizeId(row?.class_id)
      if (id && !map.has(id)) {
        map.set(id, row?.class_name || row?.class?.class_name || `Class ${id}`)
      }
    })
    return map
  }, [classes, enrollments])

  const enrollmentPairs = useMemo(() => {
    const pairs = []
    const seen = new Set()
    const pushPair = (courseId, classId, courseLabel, classLabel) => {
      const cId = normalizeId(courseId)
      if (!cId) return
      const clId = normalizeId(classId)
      const key = `${cId}|${clId}`
      if (seen.has(key)) return
      seen.add(key)
      pairs.push({
        courseId: cId,
        classId: clId,
        courseName: courseLabel || courseNameById.get(cId) || `Course ${cId}`,
        className: clId
          ? classLabel || classNameById.get(clId) || `Class ${clId}`
          : '',
      })
    }

    enrollments.forEach((row) => {
      pushPair(
        row?.course_id,
        row?.class_id,
        row?.course_name || row?.course?.name,
        row?.class_name || row?.class?.class_name,
      )
    })

    // Fallback when enroll API is empty: use profile course/class
    if (!pairs.length && student) {
      pushPair(
        student?.course_id,
        student?.class_id,
        student?.course_name,
        student?.class_name,
      )
    }

    // Optional location.state filter (legacy deep-links)
    const stateCourseId = normalizeId(location?.state?.selectedCourseId)
    const stateClassId = normalizeId(location?.state?.selectedClassId)
    if (stateCourseId) {
      const filtered = pairs.filter((pair) => {
        if (pair.courseId !== stateCourseId) return false
        if (!stateClassId) return true
        return !pair.classId || pair.classId === stateClassId
      })
      if (filtered.length) return filtered
      pushPair(
        stateCourseId,
        stateClassId,
        location?.state?.selectedCourseName,
        location?.state?.selectedClassName,
      )
      return pairs.filter((pair) => {
        if (pair.courseId !== stateCourseId) return false
        if (!stateClassId) return true
        return !pair.classId || pair.classId === stateClassId
      })
    }

    return pairs
  }, [
    enrollments,
    student,
    courseNameById,
    classNameById,
    location?.state,
  ])

  const examHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${user?.token}`,
      'institute-Id': Number(instituteId),
    }),
    [user?.token, instituteId],
  )

  const sessionOptions = useMemo(() => {
    const fromExams = [
      ...new Set(
        (Array.isArray(exams) ? exams : [])
          .map((ex) => String(ex?.session || '').trim())
          .filter(Boolean),
      ),
    ]
    const options = [...fromExams]
    if (currentSession && !options.includes(currentSession)) {
      options.unshift(currentSession)
    }
    return options
  }, [exams, currentSession])

  const examOptions = useMemo(() => {
    if (!Array.isArray(exams) || !exams.length) return []
    const norm = (v) => String(v ?? '').trim().toLowerCase()
    const pairKeys = enrollmentPairs.map((pair) => ({
      ...pair,
      courseKey: norm(pair.courseId),
      classKey: norm(pair.classId),
    }))

    const matched = []
    exams.forEach((ex) => {
      if (
        selectedSession &&
        String(ex?.session || '').trim() !== String(selectedSession).trim()
      ) {
        return
      }

      const exCourse = norm(ex?.course_id ?? ex?.courseId ?? ex?.course?.id)
      const exClass = norm(
        ex?.class_id ?? ex?.classId ?? ex?.std_id ?? ex?.standard_id ?? ex?.class?.id,
      )

      let pair = null
      if (exCourse && pairKeys.length) {
        pair = pairKeys.find((p) => {
          if (p.courseKey !== exCourse) return false
          if (!p.classKey) return true
          if (!exClass) return true
          return p.classKey === exClass
        })
      }

      const courseId =
        pair?.courseId ||
        normalizeId(ex?.course_id ?? ex?.courseId ?? ex?.course?.id) ||
        normalizeId(student?.course_id)
      const classId =
        pair?.classId ||
        normalizeId(ex?.class_id ?? ex?.classId) ||
        normalizeId(student?.class_id)

      matched.push({
        ...ex,
        _admitCourseId: courseId,
        _admitClassId: classId,
        _admitCourseName:
          pair?.courseName ||
          ex?.course?.name ||
          courseNameById.get(courseId) ||
          student?.course_name ||
          (courseId ? `Course ${courseId}` : ''),
        _admitClassName:
          pair?.className ||
          classNameById.get(classId) ||
          student?.class_name ||
          (classId ? `Class ${classId}` : ''),
      })
    })

    const timeOf = (ex) => {
      const t = new Date(ex?.start_date || ex?.exam_date || 0).getTime()
      return Number.isNaN(t) ? 0 : t
    }

    return matched.sort((a, b) => timeOf(b) - timeOf(a) || Number(b.id) - Number(a.id))
  }, [
    exams,
    enrollmentPairs,
    selectedSession,
    student,
    courseNameById,
    classNameById,
  ])

  const selectedExamOption = useMemo(() => {
    if (!selectedExamId) return null
    // Prefer session-filtered list match; fall back to raw exams for deep links
    const fromOptions =
      examOptions.find((ex) => String(ex.id) === String(selectedExamId)) || null
    if (fromOptions) return fromOptions

    const raw =
      (Array.isArray(exams) ? exams : []).find(
        (ex) => String(ex.id) === String(selectedExamId),
      ) || null
    if (!raw) return null

    const courseId =
      normalizeId(raw?.course_id ?? raw?.courseId ?? raw?.course?.id) ||
      normalizeId(student?.course_id)
    const classId = normalizeId(student?.class_id)
    return {
      ...raw,
      _admitCourseId: courseId,
      _admitClassId: classId,
      _admitCourseName:
        raw?.course?.name ||
        courseNameById.get(courseId) ||
        student?.course_name ||
        (courseId ? `Course ${courseId}` : ''),
      _admitClassName:
        classNameById.get(classId) ||
        student?.class_name ||
        (classId ? `Class ${classId}` : ''),
    }
  }, [
    examOptions,
    selectedExamId,
    exams,
    student,
    courseNameById,
    classNameById,
  ])

  const studentCourseId =
    normalizeId(selectedExamOption?._admitCourseId) ||
    normalizeId(location?.state?.selectedCourseId) ||
    normalizeId(student?.course_id)
  const studentClassId =
    normalizeId(selectedExamOption?._admitClassId) ||
    normalizeId(location?.state?.selectedClassId) ||
    normalizeId(student?.class_id)
  const courseName =
    selectedExamOption?._admitCourseName ||
    location?.state?.selectedCourseName ||
    courseNameById.get(studentCourseId) ||
    student?.course_name ||
    (studentCourseId ? `Course ${studentCourseId}` : '')
  const className =
    selectedExamOption?._admitClassName ||
    location?.state?.selectedClassName ||
    classNameById.get(studentClassId) ||
    student?.class_name ||
    (studentClassId ? `Class ${studentClassId}` : '')

  const filteredSubjects = useMemo(
    () => getFilteredSubjects(subjects, studentCourseId, studentClassId),
    [subjects, studentCourseId, studentClassId],
  )

  const subjectRows = useMemo(() => getSubjectRows(filteredSubjects), [filteredSubjects])

  // Keep list/view mode in sync with /student/:studentId/admit-card/:examId
  useEffect(() => {
    const fromParam = normalizeId(examIdParam)
    if (fromParam) {
      setSelectedExamId(String(fromParam))
      setViewMode('view')
      return
    }
    setSelectedExamId('')
    setViewMode('list')
    setSubjectSchedules([])
    setScheduleExamLocation('')
  }, [examIdParam])

  // Optional deep-link: open a specific exam when examId is passed in location.state
  useEffect(() => {
    if (appliedExamFromStateRef.current || examIdParam) return
    const fromState = normalizeId(
      location?.state?.examId ?? location?.state?.selectedExamId ?? location?.state?.exam_id,
    )
    if (!fromState || !examOptions.some((ex) => String(ex.id) === fromState)) return
    appliedExamFromStateRef.current = true
    navigate(`/student/${studentId}/admit-card/${fromState}`, { replace: true })
  }, [examOptions, location?.state, examIdParam, navigate, studentId])

  const examName = selectedExamOption?.exam_name || ''
  // Only show exam session from API — never fall back to cookie when session is null
  const sessionName = String(selectedExamOption?.session || '').trim()

  useEffect(() => {
    if (viewMode !== 'view' || !selectedExamId) {
      setSubjectSchedules([])
      setScheduleExamLocation('')
      return
    }

    // Wait until student exams payload is available
    if (loading) return

    const examFromList =
      exams.find((ex) => String(ex.id) === String(selectedExamId)) ||
      selectedExamOption ||
      null
    const nestedSchedules = Array.isArray(examFromList?.schedules)
      ? examFromList.schedules
      : []

    // Prefer schedules from /student/:id/exams — skip extra schedule API when present
    if (nestedSchedules.length > 0) {
      setSubjectSchedules(nestedSchedules)
      setScheduleExamLocation(examFromList?.exam_location || '')
      setLoadingSchedules(false)
      return
    }

    // Fallback only when the exam payload has no nested schedules
    if (!user?.token) {
      setSubjectSchedules([])
      setScheduleExamLocation(examFromList?.exam_location || '')
      setLoadingSchedules(false)
      return
    }

    let cancelled = false
    const loadSchedules = async () => {
      setLoadingSchedules(true)
      try {
        const schedRes = await axios.get(`${BASE_URL}/exam/subject-schedules`, {
          headers: examHeaders,
          params: { exam_id: Number(selectedExamId) },
        })
        if (cancelled) return
        const raw = schedRes.data?.data ?? schedRes.data ?? []
        setSubjectSchedules(Array.isArray(raw) ? raw : [])
        setScheduleExamLocation(
          schedRes.data?.exam_location || examFromList?.exam_location || '',
        )
      } catch {
        if (!cancelled) {
          setSubjectSchedules([])
          setScheduleExamLocation(examFromList?.exam_location || '')
        }
      } finally {
        if (!cancelled) setLoadingSchedules(false)
      }
    }
    loadSchedules()
    return () => {
      cancelled = true
    }
  }, [
    viewMode,
    selectedExamId,
    selectedExamOption,
    user?.token,
    examHeaders,
    exams,
    loading,
  ])

  const examRows = useMemo(() => {
    if (selectedExamId && !loadingSchedules && subjectSchedules.length > 0) {
      return mapSubjectSchedulesToAdmitRows(subjectSchedules)
    }
    return buildExamRows(subjectRows, exams, studentCourseId, studentClassId)
  }, [
    selectedExamId,
    loadingSchedules,
    subjectSchedules,
    subjectRows,
    exams,
    studentCourseId,
    studentClassId,
  ])

  const examLocation = useMemo(() => {
    if (scheduleExamLocation) return scheduleExamLocation
    if (selectedExamOption?.exam_location) return selectedExamOption.exam_location
    if (!Array.isArray(exams) || !studentCourseId) return ''
    const norm = (v) => String(v ?? '').trim().toLowerCase()
    const normalizedCourseId = norm(studentCourseId)
    const normalizedClassId = norm(studentClassId)
    const matched = exams.find((examItem) => {
      const examCourseId = norm(
        examItem?.course_id ?? examItem?.courseId ?? examItem?.course?.id,
      )
      const examClassId = norm(
        examItem?.class_id ??
          examItem?.classId ??
          examItem?.std_id ??
          examItem?.standard_id ??
          examItem?.class?.id,
      )
      const isCourseMatched = !examCourseId || examCourseId === normalizedCourseId
      const isClassMatched = !examClassId || examClassId === normalizedClassId
      return isCourseMatched && isClassMatched && examItem?.exam_location
    })
    return matched?.exam_location || ''
  }, [scheduleExamLocation, selectedExamOption, exams, studentCourseId, studentClassId])

  const handleViewAdmitCard = (examId) => {
    if (!studentId || !examId) return
    navigate(`/student/${studentId}/admit-card/${examId}`)
  }

  const handleDownloadPdf = async () => {
    if (!admitCardRef.current || exporting) return

    const safeName = String(student?.name || studentId || 'student')
      .replace(/[^\w\-]+/g, '_')
      .replace(/^_+|_+$/g, '')
    const baseFileName = `Admit-Card-${safeName || 'student'}`

    // Android/iOS WebView: blob downloads and window.print do nothing.
    // Hand the card image to the native share sheet (same bridge as receipts).
    if (isReactNativeWebView()) {
      setExporting(true)
      const toastId = toast.loading('Preparing admit card...')
      let restoreImages = () => {}
      try {
        restoreImages = await inlineImagesForCapture(admitCardRef.current)
        const canvas = await html2canvas(admitCardRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          imageTimeout: 15000,
        })

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (result) =>
              result ? resolve(result) : reject(new Error('Image failed')),
            'image/png',
          )
        })
        const file = new File([blob], `${baseFileName}.png`, {
          type: 'image/png',
        })

        const webShare = await shareFilesWithWebShare([file])
        if (webShare === 'shared') {
          toast.success('Admit card ready — choose Save or Print.', {
            id: toastId,
          })
          return
        }
        if (webShare === 'cancelled') {
          toast('Share cancelled.', { id: toastId })
          return
        }

        const result = await requestNativeImageShare([
          canvasToShareImage(canvas, baseFileName, { preferJpeg: true }),
        ])
        if (result.status === 'shared') {
          toast.success('Admit card ready — choose Save or Print.', {
            id: toastId,
          })
          return
        }
        if (result.status === 'cancelled') {
          toast('Share cancelled.', { id: toastId })
          return
        }
        throw new Error(nativeShareErrorMessage(result))
      } catch (error) {
        console.error(error)
        toast.error(
          error?.message || 'Could not prepare the admit card for download',
          { id: toastId },
        )
      } finally {
        restoreImages()
        setExporting(false)
      }
      return
    }

    const opt = {
      margin: [8, 8, 8, 8],
      filename: `${baseFileName}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, allowTaint: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] },
    }

    html2pdf().set(opt).from(admitCardRef.current).save()
  }

  const pageTitle =
    viewMode === 'view' && examName ? `Admit Card — ${examName}` : 'Admit Cards'

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (error || !student) {
    return (
      <div style={{ maxWidth: '960px', margin: '40px auto', padding: '0 16px' }}>
        <div style={{ border: '1px solid #fecaca', borderRadius: '12px', background: '#fef2f2', padding: '18px' }}>
          <h5 style={{ margin: 0, color: '#991b1b' }}>Unable to load admit card</h5>
          <p style={{ margin: '8px 0 0', color: '#7f1d1d' }}>{error || 'Student data not found.'}</p>
        </div>
        <Button className="mt-3" variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    )
  }

  return (
    <>
      <style>
        {`
          .admit-page {
            width: 100%;
            max-width: 100%;
            margin: 24px 0;
            padding: 0 20px 24px;
            box-sizing: border-box;
          }
          .admit-page.admit-page-view {
            max-width: 920px;
            margin: 24px auto;
            padding: 0 16px 24px;
          }
          .admit-toolbar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 16px;
            gap: 8px 12px;
          }
          .admit-toolbar-actions {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 8px;
            margin-left: auto;
          }
          .admit-main-wrap {
            transition: margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            z-index: 1;
            padding-left: 12px;
            padding-right: 12px;
          }
          .admit-top-header {
            margin-top: -12px;
            border-bottom: 1px solid #d1d3d4;
            background: #f3f4f6;
            padding: 12px 28px;
            width: 100%;
          }
          .admit-top-header-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
          }
          .admit-title-wrap {
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .admit-header-logo {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            overflow: hidden;
            background: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .admit-list-meta {
            margin: 0;
            color: #4b5563;
            font-size: 14px;
            font-family: Inter, system-ui, sans-serif;
            line-height: 1.4;
            min-width: 0;
            flex: 1 1 auto;
          }
          .admit-list-table-wrap {
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            overflow: hidden;
            background: #fff;
          }
          .admit-list-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .admit-list-table thead th {
            background: #f3f4f6;
            color: #374151;
            font-size: 13px;
            font-weight: 600;
            padding: 14px 16px;
            border-bottom: 1px solid #D1D3D4;
            font-family: Inter, system-ui, sans-serif;
            white-space: nowrap;
          }
          .admit-list-table tbody td {
            font-size: 14px;
            padding: 14px 16px;
            border-bottom: 1px solid #F3F4F6;
            color: #111827;
            font-family: Inter, system-ui, sans-serif;
            vertical-align: middle;
          }
          .admit-list-table tbody tr:last-child td {
            border-bottom: none;
          }
          .admit-list-mobile-card {
            border: 1px solid #D1D3D4;
            border-radius: 12px;
            background: #fff;
            margin-bottom: 12px;
            overflow: hidden;
          }
          .admit-list-mobile-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            border-bottom: 1px solid #F3F4F6;
          }
          .admit-list-mobile-row:last-child {
            border-bottom: none;
          }
          .admit-list-mobile-label {
            font-size: 13px;
            font-weight: 600;
            color: #6b7280;
            font-family: Inter, system-ui, sans-serif;
          }
          .admit-list-mobile-value {
            font-size: 14px;
            color: #111827;
            text-align: right;
            font-family: Inter, system-ui, sans-serif;
          }
          .admit-view-btn {
            display: inline-flex !important;
            align-items: center;
            gap: 6px;
            border-color: #223843 !important;
            color: #223843 !important;
            font-weight: 600;
            background-color: #ffffff !important;
            transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
          }
          .admit-view-btn:hover,
          .admit-view-btn:focus,
          .admit-view-btn:active {
            background-color: #223843 !important;
            border-color: #223843 !important;
            color: #ffffff !important;
          }
          .admit-view-btn:hover svg,
          .admit-view-btn:focus svg,
          .admit-view-btn:active svg {
            color: #ffffff !important;
            fill: currentColor;
          }
          .admit-filter-btn {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background-color: #ffffff;
            border: 1px solid #D1D3D4;
            color: #223843;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            flex-shrink: 0;
            box-shadow: none;
          }
          .admit-filter-btn:hover,
          .admit-filter-btn:focus-visible {
            background-color: #223843 !important;
            color: #ffffff !important;
            border-color: #223843 !important;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(34, 56, 67, 0.28);
            outline: none;
          }
          .admit-filter-btn.is-active {
            background-color: #223843;
            color: #ffffff;
            border-color: #223843;
          }
          .admit-filter-btn.is-active:hover,
          .admit-filter-btn.is-active:focus-visible {
            background-color: #1f323a !important;
            border-color: #1f323a !important;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(34, 56, 67, 0.35);
          }
          @media (max-width: 768px) {
            .admit-filter-btn {
              width: 44px;
              height: 44px;
            }
          }
          @media print {
            .admit-toolbar, .admit-top-header, .no-print { display: none !important; }
            .admit-page { margin: 0; padding: 0; max-width: 100%; }
            .admit-main-wrap { margin-left: 0 !important; padding: 0 !important; }
          }
        `}
      </style>

      <div className="admit-main-wrap" style={{ marginLeft }}>
        <div className="container-fluid p-0">
          <div style={{ marginLeft: isMobile ? '0' : '-40px' }}>
            <div className="admit-top-header">
              <div className="admit-top-header-row">
                <div className="admit-title-wrap">
                  <div className="admit-header-logo">
                    {!logoError ? (
                      <img
                        src={headerLogoSrc}
                        alt="School Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          // Retry once without cache-bust issues; then fall back to icon
                          const current = e.currentTarget.src
                          if (
                            instituteDisplayLogo &&
                            instituteDisplayLogo !== NO_IMAGE_SRC &&
                            current !== instituteDisplayLogo
                          ) {
                            e.currentTarget.src = instituteDisplayLogo
                            return
                          }
                          setLogoError(true)
                        }}
                      />
                    ) : (
                      <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                    )}
                  </div>
                  <h3
                    className="m-0 fw-bold"
                    style={{
                      letterSpacing: '-0.02em',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#223843',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    {pageTitle}
                  </h3>
                </div>
                <div className="d-flex align-items-center gap-3" style={{ justifyContent: 'flex-end' }}>
                  {!isMobile && <ProfileDropdown logout={logout} />}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`admit-page${viewMode === 'view' ? ' admit-page-view' : ''}`}>
          <div className="admit-toolbar no-print">
            {viewMode === 'list' && (
              <button
                type="button"
                className={`admit-filter-btn${showFilterCard ? ' is-active' : ''}`}
                onClick={() => setShowFilterCard((prev) => !prev)}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
                aria-label={showFilterCard ? 'Hide Filters' : 'Show Filters'}
                aria-pressed={showFilterCard}
              >
                <BsFunnelFill size={isMobile ? 18 : 20} />
              </button>
            )}
            {viewMode === 'view' && (
              <div className="admit-toolbar-actions">
                <Button
                  onClick={handleDownloadPdf}
                  disabled={exporting || loadingSchedules}
                  style={{
                    backgroundColor: '#223843',
                    color: '#ffffff',
                    borderColor: '#223843',
                    fontWeight: 600,
                  }}
                >
                  {exporting ? 'Preparing…' : 'Download / Print Admit Card'}
                </Button>
              </div>
            )}
          </div>

          {viewMode === 'list' && (
            <>
              <div
                className="filter-card-container"
                style={{
                  maxHeight: showFilterCard ? '1000px' : '0',
                  overflow: showFilterCard ? 'visible' : 'hidden',
                  transition: 'max-height 0.3s ease-in-out',
                  marginBottom: showFilterCard ? (isMobile ? '16px' : '24px') : '0',
                }}
              >
                {showFilterCard && (
                  <Card
                    style={{
                      border: '1px solid #D1D3D4',
                      borderRadius: '12px',
                      backgroundColor: '#ffffff',
                      overflow: 'visible',
                    }}
                  >
                    <div
                      style={{
                        background: '#ffffff',
                        padding: isMobile ? '16px' : '20px',
                        borderBottom: '1px solid #F3F4F6',
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          style={{
                            width: isMobile ? '36px' : '40px',
                            height: isMobile ? '36px' : '40px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <BsSearch
                            style={{
                              color: '#ffffff',
                              fontSize: isMobile ? '16px' : '18px',
                            }}
                          />
                        </div>
                        <div>
                          <h5
                            className="m-0 fw-semibold text-start"
                            style={{
                              color: '#111827',
                              fontSize: isMobile ? '15px' : '16px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Filter Admit Cards
                          </h5>
                          <p
                            className="m-0 mt-1"
                            style={{ fontSize: '13px', color: '#6b7280' }}
                          >
                            Filter exams by academic session
                          </p>
                        </div>
                      </div>
                    </div>

                    <Card.Body
                      className={isMobile ? 'p-3' : 'p-4'}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <Row className="g-4 text-start">
                        <Col xs={12} md={12} lg={12}>
                          <div
                            style={{
                              padding: isMobile ? '16px' : '20px',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              width: '100%',
                            }}
                          >
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  backgroundColor: '#10B981',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <BsSearch
                                  style={{ color: '#ffffff', fontSize: '16px' }}
                                />
                              </div>
                              <label
                                className="fw-semibold mb-0"
                                style={{
                                  color: '#111827',
                                  fontSize: '14px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Filter by Session
                              </label>
                            </div>
                            <div style={{ position: 'relative', width: '100%' }}>
                              <label
                                className="fw-semibold mb-2 d-block"
                                style={{
                                  color: '#6b7280',
                                  fontSize: '13px',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                              >
                                Select Session
                              </label>
                              <Form.Select
                                value={selectedSession}
                                onChange={(e) => setSelectedSession(e.target.value)}
                                style={{
                                  borderRadius: '8px',
                                  border: '1px solid #D1D3D4',
                                  fontSize: '14px',
                                  height: isMobile ? '44px' : '40px',
                                  width: '100%',
                                  maxWidth: '100%',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: '#ffffff',
                                  fontFamily: 'Inter, system-ui, sans-serif',
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = '#223843'
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#D1D3D4'
                                }}
                              >
                                <option value="">All Sessions</option>
                                {sessionOptions.map((session) => (
                                  <option key={session} value={session}>
                                    {session}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}
              </div>

              {examOptions.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '48px 16px',
                    color: '#6b7280',
                    border: '1px solid #D1D3D4',
                    borderRadius: '12px',
                    background: '#fff',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  No admit cards found for this student
                  {selectedSession ? ` in session ${selectedSession}` : ''}.
                </div>
              ) : isMobile ? (
                examOptions.map((ex, index) => {
                  const badge = statusBadgeStyle(ex?.status)
                  return (
                    <div key={`${ex.id}-${ex._admitCourseId}-${ex._admitClassId}`} className="admit-list-mobile-card">
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">S/No</span>
                        <span className="admit-list-mobile-value">{index + 1}</span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Course</span>
                        <span className="admit-list-mobile-value">
                          {ex._admitCourseName || '—'}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Class</span>
                        <span className="admit-list-mobile-value">
                          {ex._admitClassName || '—'}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Exam Name</span>
                        <span className="admit-list-mobile-value">
                          {ex.exam_name || `Exam ${ex.id}`}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Start Date</span>
                        <span className="admit-list-mobile-value">
                          {formatDate(ex?.start_date) || '—'}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">End Date</span>
                        <span className="admit-list-mobile-value">
                          {formatDate(ex?.end_date) || '—'}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Status</span>
                        <span
                          style={{
                            ...badge,
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                          }}
                        >
                          {ex?.status || '—'}
                        </span>
                      </div>
                      <div className="admit-list-mobile-row">
                        <span className="admit-list-mobile-label">Action</span>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="admit-view-btn"
                          onClick={() => handleViewAdmitCard(ex.id)}
                        >
                          <BsEyeFill size={14} />
                        </Button>
                      </div>
                    </div>
                  )
                })
              ) : (
                <div className="admit-list-table-wrap" style={{ overflowX: 'auto' }}>
                  <table className="admit-list-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>S/No</th>
                        <th style={{ textAlign: 'left' }}>Course</th>
                        <th style={{ textAlign: 'left' }}>Class</th>
                        <th style={{ textAlign: 'left' }}>Exam Name</th>
                        <th style={{ textAlign: 'left' }}>Start Date</th>
                        <th style={{ textAlign: 'left' }}>End Date</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {examOptions.map((ex, index) => {
                        const badge = statusBadgeStyle(ex?.status)
                        return (
                          <tr key={`${ex.id}-${ex._admitCourseId}-${ex._admitClassId}`}>
                            <td>{index + 1}</td>
                            <td>{ex._admitCourseName || '—'}</td>
                            <td>{ex._admitClassName || '—'}</td>
                            <td>{ex.exam_name || `Exam ${ex.id}`}</td>
                            <td>{formatDate(ex?.start_date) || '—'}</td>
                            <td>{formatDate(ex?.end_date) || '—'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span
                                style={{
                                  ...badge,
                                  padding: '5px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                  display: 'inline-block',
                                }}
                              >
                                {ex?.status || '—'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                className="admit-view-btn"
                                title="View Admit Card"
                                onClick={() => handleViewAdmitCard(ex.id)}
                              >
                                <BsEyeFill size={14} />
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {viewMode === 'view' && selectedExamId && loadingSchedules && (
            <div className="d-flex flex-column justify-content-center align-items-center gap-2 py-5 text-muted">
              <Spinner animation="border" variant="primary" />
              <span>Loading exam subject schedule…</span>
            </div>
          )}

          {viewMode === 'view' && selectedExamId && !loadingSchedules && (
            useShriHitTemplate ? (
              <AdmitCardShriHit1
                admitCardRef={admitCardRef}
                student={student}
                courseName={courseName}
                className={className}
                instituteName={instituteName}
                examName={examName}
                sessionName={sessionName}
                instituteLogoSrc={instituteLogoSrc}
                examRows={examRows}
                examLocation={examLocation}
              />
            ) : (
              <AdmitCardPrintView
                admitCardRef={admitCardRef}
                student={student}
                courseName={courseName}
                className={className}
                instituteName={instituteName}
                examName={examName}
                sessionName={sessionName}
                instituteLogoSrc={instituteLogoSrc}
                examRows={examRows}
                examLocation={examLocation}
              />
            )
          )}
        </div>
      </div>
    </>
  )
}

export default AdmitCard
